import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";

export default function Login() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("beta"); // 'beta' | 'admin'
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (tab === "beta") {
        await api.post("/auth/beta-login", { token: token.trim() });
      } else {
        await api.post("/auth/admin-login", { email: email.trim(), password });
      }
      await refresh();
      const to = location.state?.from?.pathname || (tab === "admin" ? "/admin" : "/");
      navigate(to, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img
            src="/closer-helper-logo.png"
            alt="Closer Helper"
            className="max-w-[320px] w-full h-auto"
            data-testid="login-logo"
          />
        </div>

        <form
          onSubmit={submit}
          className="bg-neutral-900/80 border border-yellow-600/30 rounded-2xl p-6 space-y-4 backdrop-blur"
          data-testid="login-form"
        >
          <div className="flex gap-1 rounded-lg bg-neutral-800/70 p-1 text-xs font-mono uppercase tracking-widest">
            <button
              type="button"
              onClick={() => setTab("beta")}
              className={`flex-1 py-2 rounded-md transition ${tab === "beta" ? "bg-yellow-500 text-black font-semibold" : "text-neutral-400 hover:text-white"}`}
              data-testid="tab-beta"
            >
              Beta access
            </button>
            <button
              type="button"
              onClick={() => setTab("admin")}
              className={`flex-1 py-2 rounded-md transition ${tab === "admin" ? "bg-yellow-500 text-black font-semibold" : "text-neutral-400 hover:text-white"}`}
              data-testid="tab-admin"
            >
              Admin
            </button>
          </div>

          {tab === "beta" ? (
            <label className="block space-y-1.5">
              <span className="text-[11px] uppercase tracking-widest text-neutral-400">Beta access token</span>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your invite token"
                autoComplete="off"
                spellCheck="false"
                className="w-full bg-neutral-950 border border-neutral-700 focus:border-yellow-500 rounded-md px-3 py-2.5 text-sm font-mono outline-none"
                data-testid="input-beta-token"
                required
              />
            </label>
          ) : (
            <>
              <label className="block space-y-1.5">
                <span className="text-[11px] uppercase tracking-widest text-neutral-400">Admin email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  className="w-full bg-neutral-950 border border-neutral-700 focus:border-yellow-500 rounded-md px-3 py-2.5 text-sm outline-none"
                  data-testid="input-admin-email"
                  required
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-[11px] uppercase tracking-widest text-neutral-400">Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full bg-neutral-950 border border-neutral-700 focus:border-yellow-500 rounded-md px-3 py-2.5 text-sm outline-none"
                  data-testid="input-admin-password"
                  required
                />
              </label>
            </>
          )}

          {error && (
            <p className="text-sm text-red-400" data-testid="login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-md bg-yellow-500 hover:bg-yellow-400 disabled:opacity-60 text-black font-semibold text-sm tracking-wide transition"
            data-testid="login-submit"
          >
            {submitting ? "Signing in…" : tab === "beta" ? "Enter beta" : "Sign in"}
          </button>
        </form>

        <p className="text-center text-[11px] text-neutral-500 mt-6 font-mono uppercase tracking-widest">
          Beta — invitation only
        </p>
      </div>
    </div>
  );
}
